import { useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DISPLAY_MONTH_NAMES } from '@/lib/payments';
import { currentMonthKey, formatMonthKey, fromMonthKey, toMonthKey } from '@/lib/months';
import { cn } from '@/lib/utils';

const YEARS_PER_PAGE = 12;

const NAV_BUTTON_CLASS =
  'flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900';

const cellClass = (selected: boolean, current: boolean) =>
  cn(
    'flex h-9 items-center justify-center rounded-md text-xs font-medium transition-colors',
    selected ? 'bg-[#4369a0] font-semibold text-white hover:bg-[#3a5d8f]' : 'text-gray-700 hover:bg-gray-100',
    current && !selected && 'font-semibold ring-1 ring-inset ring-[#4369a0]/40'
  );

interface MonthPickerPanelProps {
  /** Selected month as 'YYYY-MM', or empty for none. */
  value?: string;
  onSelect: (monthKey: string) => void;
}

/**
 * Month and year picker without days. Shows the 12 months of a year; clicking the year shows
 * a page of years, and picking a year goes back to its months.
 */
export function MonthPickerPanel({ value, onSelect }: MonthPickerPanelProps) {
  const selected = value ? fromMonthKey(value) : null;
  const today = fromMonthKey(currentMonthKey());
  const [view, setView] = useState<'months' | 'years'>('months');
  const [viewYear, setViewYear] = useState(selected?.year ?? today.year);

  // Year pages start on a multiple of 12 so the selected year stays on its page.
  const firstYear = viewYear - (((viewYear % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE);
  const step = view === 'months' ? 1 : YEARS_PER_PAGE;

  return (
    <div className="w-[280px]">
      <div className="mb-3 flex items-center justify-between">
        <button type="button" onClick={() => setViewYear((year) => year - step)} className={NAV_BUTTON_CLASS} aria-label="Previous">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setView('months')}
            className={cn('rounded-md px-2 py-1 text-sm font-semibold hover:bg-gray-100', view === 'months' ? 'text-[#4369a0]' : 'text-gray-900')}
          >
            {selected && selected.year === viewYear ? DISPLAY_MONTH_NAMES[selected.month] : 'Month'}
          </button>
          <button
            type="button"
            onClick={() => setView('years')}
            className={cn('rounded-md px-2 py-1 text-sm font-semibold hover:bg-gray-100', view === 'years' ? 'text-[#4369a0]' : 'text-gray-900')}
          >
            {view === 'years' ? `${firstYear} – ${firstYear + YEARS_PER_PAGE - 1}` : viewYear}
          </button>
        </div>
        <button type="button" onClick={() => setViewYear((year) => year + step)} className={NAV_BUTTON_CLASS} aria-label="Next">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {view === 'months' ? (
        <div className="grid grid-cols-4 gap-1.5">
          {DISPLAY_MONTH_NAMES.map((name, month) => (
            <button
              key={name}
              type="button"
              onClick={() => onSelect(toMonthKey({ month, year: viewYear }))}
              className={cellClass(
                selected?.year === viewYear && selected.month === month,
                today.year === viewYear && today.month === month
              )}
            >
              {name}
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1.5">
          {Array.from({ length: YEARS_PER_PAGE }, (_, index) => firstYear + index).map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => {
                setViewYear(year);
                setView('months');
              }}
              className={cellClass(selected?.year === year, today.year === year)}
            >
              {year}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface MonthPickerProps extends MonthPickerPanelProps {
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/** Form field that opens the month picker and shows the choice as "Oct 2026". */
export function MonthPicker({ id, value, onSelect, placeholder = 'Select month', disabled, className }: MonthPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-sm border border-gray-200 bg-white px-3 text-left text-sm transition-colors',
            'hover:border-gray-300 focus-visible:border-[#4369a0] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4369a0]/25',
            open && 'border-[#4369a0] ring-1 ring-[#4369a0]/25',
            disabled && 'cursor-not-allowed bg-gray-50 text-gray-500 hover:border-gray-200',
            className
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-[#4369a0]" />
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value ? formatMonthKey(value) : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="z-[100] w-auto rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
        <MonthPickerPanel
          value={value}
          onSelect={(monthKey) => {
            onSelect(monthKey);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
